/**
 * API de Constituição Federal
 * Baseado na documentação: PROMPT_FRONTEND_LEGISLACAO_REACT.md
 * Base URL: {API_URL}/constituicao
 */

import { API_URL } from '../configs/config';

const BASE = `${API_URL}/constituicao`;

async function handleResponse(res) {
  const text = await res.text();
  let data = null;
  if (text && text.trim()) {
    try {
      data = JSON.parse(text);
    } catch {
      // Resposta não é JSON válido
      if (!res.ok) {
        const err = new Error(text || 'Erro ao buscar dados');
        err.status = res.status;
        throw err;
      }
      return { raw: text };
    }
  }
  if (!res.ok) {
    const err = new Error(data?.error || data?.message || text || 'Erro ao buscar dados');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data ?? {};
}

/**
 * Retorna o sumário da Constituição (visão geral)
 */
export async function getSumario() {
  const res = await fetch(`${BASE}`, { method: 'GET' });
  return handleResponse(res);
}

/**
 * Lista todos os títulos de nível raiz
 */
export async function getTitulos() {
  const res = await fetch(`${BASE}/titulos`, { method: 'GET' });
  return handleResponse(res);
}

/**
 * Retorna um título por ID com filhos diretos
 */
export async function getTitulo(id) {
  const res = await fetch(`${BASE}/titulo/${id}`, { method: 'GET' });
  return handleResponse(res);
}

/**
 * Retorna um capítulo por ID com filhos diretos
 */
export async function getCapitulo(id) {
  const res = await fetch(`${BASE}/capitulo/${id}`, { method: 'GET' });
  return handleResponse(res);
}

/**
 * Lista artigos (paginado)
 */
export async function getArtigos(page = 1, size = 50) {
  const params = new URLSearchParams({ page, size });
  const res = await fetch(`${BASE}/artigos?${params}`, { method: 'GET' });
  return handleResponse(res);
}

/**
 * Retorna um artigo por ID (opcional: incluir filhos)
 */
export async function getArtigo(id, comFilhos = false) {
  const qs = comFilhos ? '?filhos=1' : '';
  const res = await fetch(`${BASE}/artigo/${id}${qs}`, { method: 'GET' });
  return handleResponse(res);
}

/**
 * Retorna artigo por número (ex: Art. 5º)
 */
export async function getArtigoPorNumero(numero) {
  const res = await fetch(`${BASE}/artigo/numero/${numero}`, { method: 'GET' });
  return handleResponse(res);
}

/**
 * Retorna qualquer elemento por ID
 */
export async function getElemento(id, comFilhos = false) {
  const qs = comFilhos ? '?filhos=1' : '';
  const res = await fetch(`${BASE}/elemento/${id}${qs}`, { method: 'GET' });
  return handleResponse(res);
}

/**
 * Retorna elemento pelo campo url (slug amigável)
 * Resolve via sumário + getTitulo/getCapitulo/getArtigo (backend não tem endpoint /url/{slug})
 * Busca recursiva: Título -> Capítulo -> Artigo (e subníveis)
 */
async function buscarEmFilhos(filhos, urlSlug, getFilhosFn) {
  if (!filhos || filhos.length === 0) return null;
  const match = filhos.find((f) => f.url === urlSlug);
  if (match) {
    if (match.tipo === 'artigo') return getArtigo(match.id, true);
    if (match.tipo === 'titulo' && match.idSuperior !== null) return getCapitulo(match.id);
    return getElemento(match.id, true);
  }
  for (const f of filhos) {
    if (f.tipo === 'artigo') continue;
    const el = (f.idSuperior != null)
      ? await getCapitulo(f.id)
      : await getElemento(f.id, true);
    const subMatch = (el?.filhos || []).find((sf) => (sf.url || '') === (urlSlug || ''));
    if (subMatch) {
      if (subMatch.tipo === 'artigo') return getArtigo(subMatch.id, true);
      if (subMatch.tipo === 'titulo' && subMatch.idSuperior !== null) return getCapitulo(subMatch.id);
      return getElemento(subMatch.id, true);
    }
    const recursivo = await buscarEmFilhos(el?.filhos || [], urlSlug);
    if (recursivo) return recursivo;
  }
  return null;
}

export async function getElementoPorUrl(urlSlug) {
  const sumario = await getSumario();
  const titulos = sumario?.titulos || [];
  const tituloRaiz = titulos.find((t) => t.url === urlSlug);
  if (tituloRaiz) return getTitulo(tituloRaiz.id);
  for (const t of titulos) {
    const tituloCompleto = await getTitulo(t.id);
    const filhos = tituloCompleto?.filhos || [];
    const resultado = await buscarEmFilhos(filhos, urlSlug);
    if (resultado) return resultado;
  }
  const err = new Error('Elemento não encontrado');
  err.status = 404;
  throw err;
}

/**
 * Retorna filhos diretos de um elemento
 */
export async function getFilhos(id, tipo = null) {
  const params = new URLSearchParams();
  if (tipo) params.set('tipo', tipo);
  const qs = params.toString() ? `?${params}` : '';
  const res = await fetch(`${BASE}/elemento/${id}/filhos${qs}`, { method: 'GET' });
  return handleResponse(res);
}

/**
 * Retorna breadcrumb do elemento
 */
export async function getBreadcrumb(id) {
  const res = await fetch(`${BASE}/elemento/${id}/breadcrumb`, { method: 'GET' });
  return handleResponse(res);
}

/**
 * Busca textual na Constituição
 */
export async function buscar(q, { tipo, titulo, page = 1, size = 20 } = {}) {
  const params = new URLSearchParams({ q, page: String(page), size: String(size) });
  if (tipo) params.set('tipo', tipo);
  if (titulo) params.set('titulo', String(titulo));
  const res = await fetch(`${BASE}/busca?${params}`, { method: 'GET' });
  const data = await handleResponse(res);
  // Garantir estrutura esperada pela UI mesmo quando API retorna corpo vazio
  if (!data || (typeof data === 'object' && !Array.isArray(data) && !('data' in data))) {
    return { data: [], total: 0, curPage: page, query: String(q || '') };
  }
  return data;
}
